import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { 
  Character, 
  CharacterFeatureSource, 
  KnowledgeFeature, 
  LearnableFeature,
} from "@dndModels";
import { ModelBase, SmartHash } from "@models";
import { DATA_TYPE_INFO } from "@dndModels/Constants";
import { DataType, isDataType } from '@dndModels/Types';

import DandDAPI from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from '@dndUtilities/char_utilities';

import CenteredMenu from "@components/input/menus/CenteredMenu";
import LoadingBox from "@components/input/LoadingBox";
import CharacterFeatureFromSource from "@dndComponents/character/features/CharacterFeatureFromSource";
import SelectObjectBox from "@components/input/selects/SelectObjectBox";
import DandDButtonBox from "@dndComponents/DandDButtonBox";
import SelectCreatureBox from "@dndComponents/select/SelectCreatureBox";

interface Props {
  character: Character; 
  selected_usage: string;
}

const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

/**
 * For each usage_key in their character knowledge, give a tab.
 * 
 * In each of those, show any knowledge from Knowledge Features with source name, 
 * clickable to pull up the CFS for it.
 * 
 * Also in each tab, if they have a Learnable Feature for it, then give controls
 * for adding to it.
 */
export default function CharacterKnowledgeForUsage({
  character,
  selected_usage,
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [reloadLearning, setReloadLearning] = useState(false);
  const [hash, setHash] = useState<SmartHash>({});
  const [view, setView] = useState("Knowledge Features");
  const [learning, setLearning] = useState<ModelBase[]>([]);

   
  useEffect(() => {
    if (!fetched) {
      load();
    }
  });
  
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  useEffect(() => {
    if (reloadLearning) {
      setReloadLearning(false);
    }
  }, [reloadLearning, setReloadLearning]);

  const load = () => {
    setLoading(true);
    setFetched(true);
    const data_types = common_util.unique_array(character.knowledge.filter(o => o.usage === selected_usage).map(o => {
      const dt_obj = DATA_TYPE_INFO.find(o2 => o2.name === o.type);
      if (dt_obj) {
        return dt_obj.data_type;
      } else if (isDataType(o.type)) {
        return o.type;
      }
      return "";
    })).filter(o => o !== "") as DataType[];
    const ids = character.knowledge.map(o => o.obj_id);
    api.getSetOfObjects({ data_types, filter: { "_id*ObjectIDs": ids }, character_options: character.character_options }).then(res => {
      setHash(res);
      setLoading(false);
    });
  }

  const renderSelectedView = (selected_view: string, selected_type: string, obj_filter: any) => {
    // debugger;
    let knowledges = character.knowledge.filter(o => o.usage === selected_usage);
    if (selected_view === "Knowledge Features") {
      knowledges = knowledges.filter(o => o.cf_id !== undefined);
      const unique_cf_ids = common_util.unique_array(knowledges.map(o => o.cf_id) as string[]);
      const cfs_map: { [cf_id: string]: string } = {};
      for (const cf_id of unique_cf_ids) {
        const knowledge = knowledges.find(o => o.cf_id === cf_id);
        if (knowledge && knowledge.cfs_id) {
          cfs_map[cf_id] = knowledge.cfs_id;
        }
      }
      return (
        <Grid container spacing={0} direction="row">
          { unique_cf_ids.map((cf_id, key) => {
            const cfs_id = cfs_map[cf_id];
            if (cfs_id) {
              const source = character.all_char_feature_sources.find(o => o.true_id === cfs_id);
              const cfb = character.char_feature_bases.find(o => o.features.find(o2 => o2.true_id === cf_id));
              if (source && cfb) {
                const char_feature = cfb.features.find(o => o.true_id === cf_id);
                if (char_feature) {
                  return (
                    <Grid
                      key={key}
                      size={{
                        xs: 12
                      }}>
                      <CharacterFeatureFromSource
                        character={character}
                        obj={char_feature}
                        source={source}
                      />
                    </Grid>
                  );
                }
              }
            }
            return null;
          })}
        </Grid>
      );
    } else {
      const dt_obj = DATA_TYPE_INFO.find(o => o.name === selected_type);
      if (dt_obj) {
        knowledges = knowledges.filter(o => o.cf_id === undefined);
        const new_filter = {...obj_filter};
        const keys = Object.keys(new_filter);
        for (const key of keys) {
          if (new_filter[key].in) {
            new_filter[key]["$in"] = new_filter[key].in;
            delete new_filter[key].in;
          } else if (new_filter[key].nin) {
            new_filter[key]["$nin"] = new_filter[key].nin;
            delete new_filter[key].nin;
          }
        }
        // debugger;
        // new_filter["_id*ObjectIDsNot"] = knowledges.map(o => o.obj_id);
        // let display_format = "{obj.true_name}";
        // switch (dt_obj.data_type) {
        //   case "creature": display_format = "{obj.true_name} (CR: {obj.challenge_rating}){obj.flying_string}"; break;
        // }
        return (
          <Grid container spacing={0} direction="row">
            { dt_obj.data_type === "creature" ?
              <>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  {/* { !reloadLearning && */}
                    <SelectCreatureBox
                      name={`Learn New Creatures`}
                      values={learning.map(o => o._id)}
                      filter={new_filter}
                      // display_format={display_format}
                      multiple
                      onChangeObject={(changed: ModelBase[]) => {
                        setLearning(changed as ModelBase[]);
                      }}
                    />
                  {/* } */}
                </Grid>
                <Grid
                  size={{
                    xs: 12
                  }}>
                  <DandDButtonBox
                    name="Add"
                    disabled={learning.length === 0}
                    onClick={() => {
                      const new_hash = hash;
                      for (const learn_me of learning) {
                        character.knowledge.push({
                          type: selected_type,
                          usage: selected_usage,
                          obj_id: learn_me._id,
                          notes: ""
                        });
                        if (new_hash[dt_obj.data_type] === undefined) {
                          new_hash[dt_obj.data_type] = [];
                        }
                        new_hash[dt_obj.data_type].push(learn_me);
                      }
                      setLearning([]);
                      setHash(new_hash);
                      character.subscription_holder.recalculate();
                      setReloading(true);
                      // setReloadLearning(true);
                    }}
                  />
                </Grid>
                { !reloading && knowledges.map((knowledge, key) => {
                  let display = knowledge.obj_id;
                  try {
                    if (dt_obj) {
                      const obj = hash[dt_obj.data_type].find(o => o._id === display);
                      if (obj) {
                        display = char_util.getStringWithReplacementsWithObject({ clean_me: "{obj.true_name} (CR: {obj.challenge_rating}){obj.flying_string}", backup: "{obj.true_name}", obj });
                      } else {
                        console.error(knowledge);  
                        debugger;
                      }
                    } else {
                      console.error(knowledge);  
                      debugger;
                    }
                  } catch (ex) {
                    console.error(ex);
                    console.error(knowledge);  
                    debugger;
                  }
                  return (
                    <Grid
                      key={key}
                      container
                      spacing={0}
                      direction="row"
                      size={{
                        xs: 12
                      }}>
                      <Grid
                        className="vCentered"
                        size={{
                          xs: 9
                        }}>{ display }</Grid>
                      <Grid
                        size={{
                          xs: 3
                        }}>
                        <DandDButtonBox 
                          name="Forget"
                          onClick={() => {
                            const new_hash = hash;
                            character.knowledge = character.knowledge.filter(o => 
                              o.type !== selected_type || 
                              o.usage !== selected_usage || 
                              o.obj_id !== knowledge.obj_id || 
                              o.cf_id !== undefined);
                            new_hash[dt_obj.data_type] = new_hash[dt_obj.data_type].filter(o => o._id !== knowledge.obj_id);
                            setHash(new_hash);
                            character.subscription_holder.recalculate();
                            setReloading(true);
                            // setReloadLearning(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                })}
              </>
            :
              <>
                <Grid
                  size={{
                    xs: 8
                  }}>
                  { !reloadLearning &&
                    <SelectObjectBox
                      name={`Learn New ${dt_obj.plural}`}
                      data_type={dt_obj.data_type}
                      values={learning.map(o => o._id)}
                      filter={new_filter}
                      // display_format={display_format}
                      multiple
                      onChangeObject={(changed: ModelBase[]) => {
                        setLearning(changed as ModelBase[]);
                      }}
                    />
                  }
                </Grid>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <DandDButtonBox
                    name="Add"
                    disabled={learning.length === 0}
                    onClick={() => {
                      const new_hash = hash;
                      for (const learn_me of learning) {
                        character.knowledge.push({
                          type: selected_type,
                          usage: selected_usage,
                          obj_id: learn_me._id,
                          notes: ""
                        });
                        if (new_hash[dt_obj.data_type] === undefined) {
                          new_hash[dt_obj.data_type] = [];
                        }
                        new_hash[dt_obj.data_type].push(learn_me);
                      }
                      setLearning([]);
                      setHash(new_hash);
                      character.subscription_holder.recalculate();
                      setReloading(true);
                      setReloadLearning(true);
                    }}
                  />
                </Grid>
                { !reloading && knowledges.map((knowledge, key) => {
                  let display = knowledge.obj_id;
                  try {
                    if (dt_obj) {
                      const obj = hash[dt_obj.data_type].find(o => o._id === display);
                      if (obj) {
                        display = (obj as ModelBase).name;
                      } else {
                        console.error(knowledge);  
                        debugger;
                      }
                    } else {
                      console.error(knowledge);  
                      debugger;
                    }
                  } catch (ex) {
                    console.error(ex);
                    console.error(knowledge);  
                    debugger;
                  }
                  return (
                    <Grid
                      key={key}
                      container
                      spacing={0}
                      direction="row"
                      size={{
                        xs: 12
                      }}>
                      <Grid
                        className="vCentered"
                        size={{
                          xs: 9
                        }}>{ display }</Grid>
                      <Grid
                        size={{
                          xs: 3
                        }}>
                        <DandDButtonBox 
                          name="Forget"
                          onClick={() => {
                            const new_hash = hash;
                            character.knowledge = character.knowledge.filter(o => 
                              o.type !== selected_type || 
                              o.usage !== selected_usage || 
                              o.obj_id !== knowledge.obj_id || 
                              o.cf_id !== undefined);
                            new_hash[dt_obj.data_type] = new_hash[dt_obj.data_type].filter(o => o._id !== knowledge.obj_id);
                            setHash(new_hash);
                            character.subscription_holder.recalculate();
                            setReloading(true);
                            setReloadLearning(true);
                          }}
                        />
                      </Grid>
                    </Grid>
                  );
                })}
              </>
            }
          </Grid>
        );
      }
    }
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    let selected_view = "";
    let selected_type = "";
    let obj_filter: any = {};
    const view_options: string[] = [];
    const knowledge_feature = character.knowledge_features.find(o => 
      o.floating_feature instanceof KnowledgeFeature && 
      o.floating_feature.usage_key === selected_usage);
    const learnable_feature = character.learnable_features.find(o => 
      o.floating_feature instanceof LearnableFeature && 
      o.floating_feature.usage_key === selected_usage);
    if (knowledge_feature && knowledge_feature.floating_feature instanceof KnowledgeFeature) {
      view_options.push("Knowledge Features");
      selected_type = knowledge_feature.floating_feature.type;
    }
    if (learnable_feature && learnable_feature.floating_feature instanceof LearnableFeature) {
      view_options.push("Learned Knowledge");
      selected_type = learnable_feature.floating_feature.type;
      obj_filter = learnable_feature.floating_feature.obj_filter;
    } else {
      const spell = character.spells.find(o => o.knowledge_usage_key === selected_usage);
      if (spell) {
        view_options.push("Learned Knowledge");
        selected_type = spell.knowledge_type;
        obj_filter = spell.obj_filter;
      } else {
        const spell_as_ability = character.spell_as_abilities.find(o => o.knowledge_usage_key === selected_usage);
        if (spell_as_ability) {
          view_options.push("Learned Knowledge");
          selected_type = spell_as_ability.knowledge_type;
          obj_filter = spell_as_ability.obj_filter;
        } else {
          const spellbook_class = character.classes.find(o => o.spellbook && o.spellbook.knowledge_usage_key === selected_usage);
          if (spellbook_class) {
            view_options.push("Spell Book");
            selected_type = "Spell"
          }
        }
      }
    }
    if (view_options.length > 1) {
      selected_view = view;
    } else if (view_options.length === 1) {
      selected_view = view_options[0];
    }
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={view_options}
            selected={selected_view}
            onSelect={setView}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>{ renderSelectedView(selected_view, selected_type, obj_filter) }</Grid>
      </Grid>
    );
  }
}
